import { Component, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEventBusService, hd, IDeviceModel } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { WorkPanelManagerService, WorkPanelType } from 'app/modules/remote-control/services/work-panel-manager.service';
import { RcDeviceService } from 'app/modules/remote-control/services/rc-device-service';
import { RemoteControlService } from 'app/modules/remote-control/services/remote-control.service';
import { FileManagerService } from 'app/modules/remote-control/services/file-manager.service';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';

@Component({
  selector: 'xs-snapshot-title-bar',
  templateUrl: './snapshot-title-bar.component.html',
  styleUrls: ['./snapshot-title-bar.component.scss'],
})
export class SnapshotTitleBarComponent {
  @Input() device: IDeviceModel;
  @Input() isLicenceActive: boolean = true;
  @Input() mcName: string;
  @Input() date: string;
  @Input() isLiveSnapshot: boolean;
  @Input() popOut = false;
  @Input() isPopInOutDisabled: boolean;
  @Input() isMinimizeButtonVisible: boolean;
  @Input() isSnapshotRefreshInProgress: boolean;
  @Output() closeSnapshotEvent = new EventEmitter<void>();
  @Output() minimizeSnapshotEvent = new EventEmitter<void>();
  @ViewChild('snapshotTitleBar', { static: true }) snapshotTitleBar: ElementRef<HTMLDivElement>;

  private minWidthToShowTitle = 616;
  private iconSizeClass = 'icon-md';
  public get showPopout(): boolean {
    const device = this.device;
    return (
      !!device &&
      this.rcService.isPoppedOutAllowed({
        deviceId: this.device.deviceId,
        mcDeviceId: this.device.mcDeviceId,
      } as hd.IDeviceModel)
    );
  }
  public UploadOrDownloadInProgress = (): boolean => this.fileManagerService.isCommandInProgress;

  constructor(
    private globalEventBusService: GlobalEventBusService,
    private deviceService: RcDeviceService,
    private saI18nPipe: XsI18nPipe,
    private datePipe: DatePipe,
    private workPanelManagerService: WorkPanelManagerService,
    private rcService: RemoteControlService,
    private fileManagerService: FileManagerService,
    private deviceStateRegistry: DeviceStateRegistryService,
  ) {}

  hideSnapshotTitle = () => {
    if (
      this.snapshotTitleBar.nativeElement.offsetWidth &&
      this.snapshotTitleBar.nativeElement.offsetWidth < this.minWidthToShowTitle
    ) {
      return true;
    }
    return false;
  };

  getDeviceIconClass = (): string => {
    const deviceFamily = this.getDeviceFamily();
    if (!deviceFamily) {
      return '';
    }

    return this.deviceService.getDeviceIconClass(deviceFamily, this.iconSizeClass);
  };

  getDeviceScreenIconClass = (): string => {
    const deviceFamily = this.getDeviceFamily();
    if (!deviceFamily) {
      return '';
    }

    let deviceScreenIconClass = this.deviceService.getDeviceScreenIconClass(deviceFamily, this.iconSizeClass);
    if (deviceScreenIconClass !== '') {
      deviceScreenIconClass += ' online';
    }
    return deviceScreenIconClass;
  };

  getPopoutToolTip = () => {
    if (this.UploadOrDownloadInProgress() || this.snapshotRecording() || this.isSnapshotRefreshInProgress) {
      return this.saI18nPipe.transform('remoteControlPopout_Disabled_While_Busy');
    }
  };

  getPopinToolTip = () =>{
    if (this.UploadOrDownloadInProgress()) {
      return this.isPopInOutDisabled ? this.saI18nPipe.transform('snapshotPopin_Disabled_While_Busy') : '';
    }
  }

  snapshotRecording = () => {
    return this.deviceStateRegistry.getState(this.device.deviceId)?.recording;
  };

  minimizeSnapshot = () => {
    this.minimizeSnapshotEvent.emit();
  };

  popOutSnapshot = () => {
    if (this.isPopOutVisible() && this.isPopOutEnabled() && !this.isSnapshotRefreshInProgress &&  !this.UploadOrDownloadInProgress()) {
      this.globalEventBusService.broadcast(GlobalEvents.popOutStarted, WorkPanelType.liveSnapshot);
    }
  };

  popInSnapshot = () => {
    if (this.isPopInVisible() && this.isPopInEnabled() && !this.isSnapshotRefreshInProgress) {
      this.globalEventBusService.broadcast(GlobalEvents.popInStarted, WorkPanelType.liveSnapshot);
    }
  };

  closeSnapshot = () => {
    this.closeSnapshotEvent.emit();
  };

  getDeviceName = (): string => (this.device ? this.device.deviceName : '');

  getDate = (): string => {
    if (this.isLiveSnapshot) {
      return this.date;
    } else {
      return this.datePipe.transform(
        this.date,
        this.saI18nPipe.transform('timeFormatFilter_Long_MonthDayYearHourMinutes_Format')
      );
    }
  };

  isPopOutVisible = (): boolean =>
    this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.liveSnapshot) && this.showPopout;

  isPopInVisible = (): boolean =>
    this.popOut && this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.liveSnapshot);

  isCloseVisible = (): boolean =>
    !this.popOut &&
    (this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.liveSnapshot) ||
      this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.savedSnapshot));

  isPopOutEnabled = (): boolean => !this.isPopInOutDisabled;

  isPopInEnabled = (): boolean => !this.isPopInOutDisabled;

  getDeviceURL = (): string => {
    if (this.device) {
      return this.device.deviceDetailsUrl;
    } else {
      return '';
    }
  };

  getDeviceFamily = (): string => {
    if (this.device) {
      return this.deviceService.getDeviceFamily(this.device.kind, this.device.family);
    }

    return '';
  };
}
